import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO = "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGJ1aWxkaW5nJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MTc4ODE0MHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Residential society" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2942]/95 via-[#0F2942]/60 to-[#0F2942]/30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <span className="font-display text-xl font-bold tracking-tight">EstatePulse</span>
          </div>
          <div className="space-y-4 max-w-md">
            <p className="text-[11px] uppercase font-mono tracking-[0.18em] text-sky-300">Society Flat &amp; Tenancy Registry</p>
            <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight">
              Every flat. Every owner. Every move-in and move-out.
            </h1>
            <p className="text-slate-300 leading-relaxed">
              A tower-wise, floor-indexed inventory for your housing society — with full owner dossiers and the complete rental history of each unit.
            </p>
          </div>
          <p className="text-xs text-slate-400 font-mono">Secure admin access • Role-based accounts</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-7">
          <div className="lg:hidden flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#0F2942] flex items-center justify-center text-white">
              <Building2 className="h-5 w-5" />
            </div>
            <span className="font-display text-xl font-bold tracking-tight text-slate-900">EstatePulse</span>
          </div>

          <div className="space-y-2">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Sign in</h2>
            <p className="text-sm text-slate-500">Enter your admin or staff credentials to continue.</p>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="login-error">{error}</div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@society.com" required className="h-11" data-testid="admin-login-email-input" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-slate-600">Password</Label>
              <Link to="/forgot-password" className="text-xs text-[#0F2942] hover:underline font-medium" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="h-11" data-testid="admin-login-password-input" />
          </div>

          <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#0F2942] gap-2 text-base" data-testid="admin-login-submit-button">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </Button>

          <p className="text-xs text-slate-400 text-center">Accounts are created by your society administrator.</p>
        </form>
      </div>
    </div>
  );
}
